/**
 * Async lookup loaders for the Inspection Series FilterBar.
 *
 * Buildings and accounts have server-side search endpoints that support
 * pagination and text filtering, so they use real async loaders (same as
 * the Inspections feature).
 *
 * Territories are passed as a static prop array from the server; use
 * createArrayLoader() for those.
 *
 * Re-exports createArrayLoader from the Inspections feature for convenience
 * so Series code does not need to reach across feature boundaries.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import api from '@utils/axios';
import { loadAccountsByIds, loadBuildingsByIds } from '@helpers/lookupByIds';
export { createArrayLoader } from '../adapters/createArrayLoader';
const LOOKUP_PAGE_SIZE = 25;
/* eslint-enable @typescript-eslint/naming-convention, camelcase */
/**
 * Creates a `loadOptions` function for the buildings PagedMultiSelectFilter.
 * Calls /lookup/buildings with Kendo-style filter params.
 *
 * Building options include `subtitle` (address) per contract §Filter Option rule 3.
 */
export function createBuildingsLoader() {
    return (query, cursor) => __awaiter(this, void 0, void 0, function* () {
        const skip = cursor ? parseInt(cursor, 10) : 0;
        const params = {
            take: LOOKUP_PAGE_SIZE,
            skip
        };
        if (query.trim()) {
            params['filter[filters][0][value]'] = query.trim();
        }
        const response = yield api.get('/lookup/buildings', { params });
        // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
        const { total_count: totalCount, data } = response.data;
        const options = data.map((b) => {
            var _a;
            return ({
                value: b.value,
                label: b.label,
                subtitle: (_a = b.address) !== null && _a !== void 0 ? _a : undefined
            });
        });
        const nextOffset = skip + LOOKUP_PAGE_SIZE;
        const hasMore = nextOffset < totalCount;
        return {
            options,
            nextCursor: hasMore ? String(nextOffset) : undefined
        };
    });
}
/* eslint-enable @typescript-eslint/naming-convention, camelcase */
/**
 * Creates a `loadOptions` function for the accounts PagedMultiSelectFilter.
 * Calls /lookup/accounts with Kendo-style filter params.
 */
export function createAccountsLoader() {
    return (query, cursor) => __awaiter(this, void 0, void 0, function* () {
        const skip = cursor ? parseInt(cursor, 10) : 0;
        const params = {
            take: LOOKUP_PAGE_SIZE,
            skip
        };
        if (query.trim()) {
            params['filter[filters][0][value]'] = query.trim();
        }
        const response = yield api.get('/lookup/accounts', { params });
        // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
        const { total_count: totalCount, data } = response.data;
        const options = data.map((a) => ({
            value: a.value,
            label: a.label
        }));
        const nextOffset = skip + LOOKUP_PAGE_SIZE;
        const hasMore = nextOffset < totalCount;
        return {
            options,
            nextCursor: hasMore ? String(nextOffset) : undefined
        };
    });
}
export function createBuildingsByIdsLoader() {
    return (ids) => loadBuildingsByIds(ids);
}
export function createAccountsByIdsLoader() {
    return (ids) => loadAccountsByIds(ids);
}
